import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { post } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { caminhoDaCategoria, categoriaLancavel, useCategorias, useCentros } from './dados';
import { invalidarFinanceiro, type LinhaExtrato } from './tipos';

/**
 * Fase 2, W7 (B6): lançar a partir da linha do extrato numa operação só. Destino lançamento: entrada ou saída já
 * conciliada (tarifa, imposto, rendimento). Destino documento: fatura ou conta a pagar no Contas, baixada na data da
 * linha. A chave de idempotência nasce com a tela: reenviar não lança duas vezes.
 */
export function LancarDaLinha({ linha, aoFechar }: { linha: LinhaExtrato | null; aoFechar: () => void }) {
  if (!linha) return null;
  return <Corpo key={linha.id} linha={linha} aoFechar={aoFechar} />;
}

function Corpo({ linha, aoFechar }: { linha: LinhaExtrato; aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const categorias = useCategorias();
  const centros = useCentros();
  const [chave] = useState(() => crypto.randomUUID());
  const credito = linha.valor_centavos > 0;
  const tipoCategoria = credito ? 'receita' : 'despesa';
  const todas = categorias.data?.itens ?? [];
  const opcoesCategoria = todas
    .filter((c) => categoriaLancavel(c, todas, tipoCategoria))
    .map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, todas) }));
  const comContas = s.instalado('contas') && s.pode('contas.criar') && s.pode('contas.baixar');

  const campos: DefCampo[] = [
    {
      nome: 'destino',
      rotulo: t('lancarLinha.destino'),
      tipo: 'selecao',
      obrigatorio: true,
      opcoes: [{ valor: 'lancamento', rotulo: t('lancarLinha.lancamento') }, ...(comContas ? [{ valor: 'documento', rotulo: t('lancarLinha.documento') }] : [])],
    },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', obrigatorio: true },
    { nome: 'data_competencia', rotulo: t('campos.dataCompetencia'), tipo: 'data', obrigatorio: true, largura: 'meia', ajuda: t('ajuda.dataCompetencia') },
    { nome: 'categoria_id', rotulo: t('campos.categoria'), tipo: 'selecao', largura: 'meia', opcoes: opcoesCategoria, ajuda: credito ? t('ajuda.categoriaReceita') : t('ajuda.categoriaDespesa') },
    { nome: 'centro_custo_id', rotulo: t('campos.centroCusto'), tipo: 'selecao', largura: 'meia', opcoes: (centros.data?.itens ?? []).filter((c) => c.ativo).map((c) => ({ valor: c.id, rotulo: c.nome })) },
    {
      nome: 'parceiro_id',
      rotulo: t('campos.parceiro'),
      tipo: 'busca',
      largura: 'meia',
      busca: { caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome) },
    },
  ];
  const carregando = categorias.isPending || centros.isPending;
  const falha = categorias.isError ? categorias : centros.isError ? centros : null;

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('lancarLinha.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-lancar-linha" ocupado={carregando || !!falha}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 flex min-w-0 items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm">
        <span className="min-w-0 truncate" title={descricaoGerada(linha.descricao)}>{descricaoGerada(linha.descricao)}</span>
        <span className={`numero shrink-0 font-medium ${credito ? 'text-verde' : 'text-perigo'}`}>{dinheiro(linha.valor_centavos)}</span>
      </p>
      {falha ? (
        <Erro erro={falha.error} aoTentar={() => void Promise.all([categorias.refetch(), centros.refetch()])} />
      ) : carregando ? (
        <Carregando />
      ) : (
        <Formulario
          id="form-lancar-linha"
          campos={campos}
          inicial={{ destino: 'lancamento', descricao: linha.descricao, data_competencia: linha.data }}
          aoEnviar={async (v) => {
            await post(
              `/financeiro/conciliacao/linhas/${linha.id}/lancar`,
              {
                destino: v.destino,
                descricao: v.descricao,
                data_competencia: v.data_competencia,
                categoria_id: v.categoria_id || null,
                centro_custo_id: v.centro_custo_id || null,
                parceiro_id: v.parceiro_id || null,
              },
              { 'Idempotency-Key': chave },
            );
            await invalidarFinanceiro(qc);
            await qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/contas') });
            toast.sucesso(t('lancarLinha.lancado'));
            aoFechar();
          }}
        >
          {comContas && <p className="text-xs text-tinta-3 sm:col-span-6">{t('lancarLinha.ajudaDocumento')}</p>}
        </Formulario>
      )}
    </Drawer>
  );
}
